<template>
  <div v-if="products.length > 0" class="express-checkout">
    <div class="express-checkout-preview" :class="{ 'has-label': !!ctaLabel }">
      <span v-if="ctaLabel" class="cta-label">{{ ctaLabel }}</span>
      <p class="from-label">
        vanaf
      </p>
      <h2 class="price">
        <span v-if="totalPriceAfterDiscount !== totalPrice">{{ totalPrice | localize }}</span>
        {{ totalPriceAfterDiscount | localize }} 
      </h2>
      <p v-if="selectedDuration" class="monthly-price">
        {{ productDurationsLabels[selectedDuration] }} | {{ periodPricesAfterDiscount[selectedDuration] | localize }} / maand
      </p>
      <u-button @click="SET_MODAL(true)">{{ ctaButton }}</u-button>  
    </div>
    <transition name="slide-in">
      <div v-show="showModal" class="express-checkout-modal">
        <div class="modal-content">
          <button class="close-modal" @click="closeModal">
            <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.3 12.179a.25.25 0 010-.354l9.263-9.262A1.501 1.501 0 0021.44.442L12.177 9.7a.25.25 0 01-.354 0L2.561.442A1.5 1.5 0 00.44 2.563L9.7 11.825a.252.252 0 01.054.273.25.25 0 01-.054.081L.44 21.442a1.501 1.501 0 002.121 2.121l9.262-9.263a.25.25 0 01.354 0l9.262 9.263a1.5 1.5 0 102.122-2.121l-9.26-9.263z" fill="currentColor"/>
            </svg>
          </button>
          <status
            v-if="showStatus"
            :status="orderStatus"
            @hide-status="showStatus = false"
          />
          <div v-else>
            <steps-navigation
              :labels="steps"
              :active-index="currentStep"
              :max-allowed="maxAllowedStep"
              @update="updateStep"
            />
            <component :is="componentLoader" :namespace="namespace" />
            <button-navigation :namespace="namespace" />
          </div>
          <transition name="scale">
            <form-login v-if="showLogin" :namespace="namespace" />
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

  // TODO: split cta and modal in separate components

  import { createNamespacedHelpers } from 'vuex'
  import UButton from './UButton'
  import Status from './Status'
  import FormLogin from './FormLogin'
  import ExpressCheckoutModule from '../store/expressCheckout'
  import StepsNavigation from './StepsNavigation'
  import DurationOptions from './DurationOptions'
  import PersonalDetails from './PersonalDetails'
  import PaymentOptions from './PaymentOptions'
  import ButtonNavigation from './ButtonNavigation'

  export default {
    components: {
      UButton,
      Status,
      FormLogin,
      StepsNavigation,
      DurationOptions,
      PersonalDetails,
      PaymentOptions,
      ButtonNavigation
    },
    props: {
      namespace: {
        type: String,
        required: true
      },
      products: {
        type: Array,
        required: true
      },
      combiDiscounts: {
        type: Array,
        default: () => []
      },
      discounts: {
        type: Array,
        default: () => []
      },
      orderSubmitted: {
        type: Boolean,
        default: false
      },
      orderStatus: {
        type: String,
        default: ''
      },
      ctaLabel: {
        type: String,
        default: ''
      },
      ctaButton: {
        type: String,
        default: 'Activeer direct'
      },
      selectedProductId: {
        type: Number,
        default: null
      }
    },
    data () {
      return {
        showStatus: false
      }
    },
    watch: {
      showModal () {
        if (this.showModal) {
          const scrollY = window.scrollY
          document.body.style.position = 'fixed'
          document.body.style.top = `-${scrollY}px`
        } else {
          const scrollY = document.body.style.top
          document.body.style.position = ''
          document.body.style.top = ''
          window.scrollTo(0, parseInt(scrollY || '0') * -1)
        }
      }
    },
    computed: {
      componentLoader () {
        const components = [PersonalDetails, PaymentOptions]
        if (this.showDurationOptions) components.unshift(DurationOptions)
        return components[this.currentStep]
      },
      maxAllowedStep () {
        const personalDetailsIndex = this.showDurationOptions ? 1 : 0
        if (this.personalDetailsError) return personalDetailsIndex
        return this.steps.length - 1
      }
    },
    methods: {
      updateStep (num) {
        if (num >= 0 && num < this.steps.length) {
          this.GOTO_STEP(num)
        }
      },
      closeModal () {
        this.SET_MODAL(false)
        window.history.replaceState(null, null, window.location.pathname)
        setTimeout(() => {
          this.showStatus = false
        }, 250)
      }
    },
    beforeCreate () {
      // not "easy" to map properly dynamic namespaces
      // chose this approach for now
      // https://github.com/vuejs/vuex/issues/863#issuecomment-542657151
      const namespace = this.$options.propsData.namespace
      if (!this.$store.state[namespace]) {
        this.$store.registerModule(namespace, ExpressCheckoutModule)
      }
      const { mapState, mapGetters, mapMutations} = createNamespacedHelpers(namespace)
      // computed 
      this.$options.computed = {
        ...(this.$options.computed || {}),
        ...mapState(['currentStep', 'showModal', 'showLogin']),
        ...mapGetters([
          'steps',
          'showDurationOptions',
          'personalDetailsError',
          'productDurationsLabels',
          'selectedDuration',
          'totalPrice',
          'totalPriceAfterDiscount',
          'periodPricesAfterDiscount'
        ])
      }
      // methods
      this.$options.methods = {
        ...(this.$options.methods || {}),
        ...mapMutations([
          'SET_ID',
          'SET_STEPS',
          'GOTO_STEP',
          'SET_PRODUCTS',
          'SET_MODAL',
          'SET_COMBI_DISCOUNTS',
          'SET_DISCOUNTS',
          'SET_SELECTED_PRODUCT_ID'
        ])
      }
    },
    beforeDestroy() {
      // Unregister the dynamically created module
      this.$store.unregisterModule(this.namespace)
    },
    created () {
      if (this.selectedProductId) {
        this.SET_SELECTED_PRODUCT_ID(this.selectedProductId)
      }

      this.SET_PRODUCTS(this.products)
      this.SET_ID(this.namespace)
      this.SET_COMBI_DISCOUNTS(this.combiDiscounts)
      this.SET_DISCOUNTS(this.discounts)

      this.showStatus = this.orderSubmitted
    },
    mounted () {
      if (this.orderSubmitted) this.SET_MODAL(true)
    }
  }
</script>

<style lang="scss">

  @import '../assets/styles/_variables.scss';
  @import '../assets/styles/_mixins.scss';

  .express-checkout {
    display: inline-block;
    font-family: var(--ec-base-font, inherit);
    font-size: var(--ec-base-font-size, 16px);
    color: var(--ec-base-font-color, black);
    max-width: 384px;
    width: 100%;
    line-height: 1.5;

    .steps-navigation {
      padding-bottom: 1.5em;

      @include screen(md) {
        padding-bottom: 6em;
      }

    }

    h1, h2, h3, h4, h5, h6 {
      font-family: var(--ec-headings-font, inherit);
      margin: 0;
      padding: 0;
    }

    p {
      margin: 0;
      padding: 0;
    }

    .express-checkout-preview {
      position: relative;
      text-align: center;
      background: white;
      padding: 2.5em 2.5em 1.5em;
      border-radius: 12px;

      &.has-label {
        margin-top: 15px;
      }

      @include screen(md) {
        padding: 3em;
      }

      .cta-label {
        position: absolute;
        top: -15px;
        left: 50%;
        transform: translateX(-50%);
        font-size: .75em;
        font-weight: 700;
        background-color: var(--ec-base-font-color, black);
        color: white;
        padding: 6px 10px;
        min-width: 110px;
        border-radius: 44px;
      }

      .from-label {
        color: var(--ec-gray, lighten(black, 35));
        margin-bottom: .5em;
      }

      .price {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1.75em;
        line-height: 1.2;
        margin-bottom: .5em;

        @include screen(md) {
          font-size: 2.25em;
          margin-bottom: .4em;
        }

        span {
          font-size: .575em;
          font-weight: 400;
          color: var(--ec-red, red);
          text-decoration: line-through;
          margin-right: .25em;

          @include screen(md) {
            font-size: .5em;
          }

        }

      }

      .monthly-price {
        color: var(--ec-gray, lighten(black, 35));
        margin-bottom: 1.5em;

        @include screen(md) {
          margin-bottom: 2em;
        }

      }

    }

    .express-checkout-modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: flex-start;
      overflow-y: auto;
      z-index: 999;
      background-color: rgba(black, 0.1);

      @include screen(md) {
        padding: 3em 0;
      }

    }

    .modal-content {
      position: relative;
      background-color: white;
      width: 720px;
      max-width: 100%;
      min-height: 100vh;
      padding: 5em 0;
      border-radius: var(--ec-modal-border-radius, 0);

      @include screen(md) {
        padding: 3em 0;
        min-height: auto;
      }

      @include screen(lg) {
        width: 982px;
      }

    }

    .close-modal {
      display: flex;
      position: absolute;
      top: 2em;
      right: 2em;
      background: transparent;
      border: 0;
      padding: 0;
      outline: none;
      cursor: pointer;
    }

    .slide-in-enter-active, .slide-in-leave-active {
      transition: .25s cubic-bezier(0.22, 0.61, 0.36, 1);
      .modal-content {
        transition: .25s cubic-bezier(0.22, 0.61, 0.36, 1);
        transform: translateY(0);
      }
    }
    .slide-in-enter, .slide-in-leave-to {
      opacity: 0;
      .modal-content {
        transform: translateY(5em);
      }
    }

    .scale-enter-active, .scale-leave-active {
      transition: .25s cubic-bezier(0.22, 0.61, 0.36, 1);
      .form-login {
        transition: .25s cubic-bezier(0.22, 0.61, 0.36, 1);
        transform: scale(1);
      }
    }
    .scale-enter, .scale-leave-to {
      opacity: 0;
      .form-login {
        transform: scale(.85);
      }
    }

  }

</style>
